// @ts-strict-ignore
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Spacer, Typography, Link } from "@aviary";
import { AppleSSOButton } from "@shared/components/LoginOptions/AppleSSOButton/AppleSSOButton";
import { GoogleSSOButton } from "@shared/components/LoginOptions/GoogleSSOButton/GoogleSSOButton";
import { MagicLinkButton } from "@shared/components/LoginOptions/MagicLinkButton/MagicLinkButton";
import { useFlippers } from "@shared/context/FlippersContext";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useHydrated } from "@shared/hooks/useHydrated/useHydrated";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import type { SignInAdditionalAttributesType } from "@shared/types/SignInAdditionalAttributesType";
import type { TwoFactorAuthenticationMethods } from "@shared/types/graphqlGenerated";
import { AccountLockedMessage } from "@unauthenticated/shared/components/AccountLockedMessage/AccountLockedMessage";
import { ActivateKitMessage } from "@unauthenticated/shared/components/ActivateKitMessage/ActivateKitMessage";
import { ErrorBox } from "@unauthenticated/shared/components/ErrorBox/ErrorBox";
import { FormWrapper } from "@unauthenticated/shared/components/FormWrapper/FormWrapper";
import type { SsoErrorPage } from "@unauthenticated/shared/components/SSOErrorBox/types/ssoErrorPage";
import { SignInForm } from "@unauthenticated/shared/components/SignInForm/SignInForm";
import { SignInWrapper } from "@unauthenticated/shared/components/SignInWrapper/SignInWrapper";
import { UnauthorizedErrorBox } from "@unauthenticated/shared/components/UnauthorizedErrorBox/UnauthorizedErrorBox";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { l } from "@unauthenticated/shared/locales/i18n";

import { SSOErrorBox } from "../SSOErrorBox/SSOErrorBox";

import * as styles from "./OIDCSignInBlock.styles";

interface TwoFactorData {
  otpMethod: TwoFactorAuthenticationMethods;
  userEmail: string;
  emailMasked: string;
  phoneLast4: string;
  authToken: string;
}

interface Props {
  additionalAttributes?: SignInAdditionalAttributesType;
  ssoErrorPage?: SsoErrorPage;
  ssoErrors?: string;
  authErrors?: string;
  onCompleted?: (data) => void;
  onSignUpRedirect?: () => void;
  isOAuth?: boolean;
  onShowTwoFactorFlow?: (data: TwoFactorData) => void;
  isLockedOut?: boolean;
  branding?: {
    title?: string;
    subtitle?: string;
    customSignup?: {
      message?: string;
      link?: string;
      href?: string;
    };
  };
}

const OIDCSignInBlock = ({
  additionalAttributes,
  ssoErrorPage,
  ssoErrors,
  authErrors,
  onCompleted,
  onSignUpRedirect,
  isOAuth,
  onShowTwoFactorFlow,
  isLockedOut,
}: Props) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { tablet, phoneLarge } = useBreakpoints();
  const [hasInputErrors, setHasInputErrors] = useState(false);
  const { isEmerson } = useSharedGlobalConfig();
  const hydrated = useHydrated();
  const [passwordlessDisabled] = useFlippers("iam_disable_passwordless_in_oauth");

  const isLabKitActivation = pathname === authRoutes.activateKit;

  const renderError = () => {
    const unauthorized = new URLSearchParams(search).get("error");
    if (unauthorized) return <UnauthorizedErrorBox />;

    if (ssoErrors) {
      return (
        <SSOErrorBox
          ssoErrorPage={ssoErrorPage}
          ssoErrors={ssoErrors}
          onRedirect={onSignUpRedirect}
        />
      );
    }

    if (authErrors && !hasInputErrors) return <ErrorBox error={authErrors} />;

    if (isLockedOut) {
      return (
        <div css={styles.accountLockedWrapper}>
          <AccountLockedMessage />
        </div>
      );
    }
  };

  const renderPasswordless = () => {
    if (passwordlessDisabled) {
      return null;
    } else {
      return (
        <MagicLinkButton
          buttonStyles={styles.continueWithButton}
          isOrTextRendered
          isUnauthenticated
        />
      );
    }
  };

  return (
    <FormWrapper>
      <div css={styles.fullWidth}>
        <Typography
          css={styles.textCenter}
          isFullWidth
          type="h2"
          sizeOverride={tablet.lessThan ? "h3" : null}
        >
          {t(l.signIn.fullscriptAcademy.title)}
        </Typography>
        <Typography css={styles.textCenter} isFullWidth>
          {t(l.signIn.fullscriptAcademy.subtitle)}
        </Typography>
        {!!phoneLarge.greaterThan && <Spacer height="one" />}
        {!!isLabKitActivation && <ActivateKitMessage />}
        {renderError()}
        {!isEmerson && !!hydrated && (
          <>
            <GoogleSSOButton additionalAttributes={additionalAttributes} />
            <AppleSSOButton
              css={styles.continueWithButton}
              additionalAttributes={additionalAttributes}
              isOrTextRendered={passwordlessDisabled}
            />
            {renderPasswordless()}
          </>
        )}
        <SignInWrapper>
          <SignInForm
            additionalAttributes={additionalAttributes}
            setHasInputErrors={setHasInputErrors}
            forgotPasswordLink={
              isEmerson ? authRoutes.emerson.forgot_password : authRoutes.forgot_password
            }
            onCompleted={onCompleted}
            isOAuth={isOAuth}
            onShowTwoFactorFlow={onShowTwoFactorFlow}
          />
          <Spacer height="doubleHalf" />
          <Typography>
            {t(l.signIn.fullscriptAcademy.footer)}{" "}
            <Link
              css={styles.link}
              data-e2e="link-to-custom-signup-action"
              isUnderLined={false}
              isColor="success"
              to="https://fullscript.com/lp/academy-fullscript"
            >
              {t(l.signIn.fullscriptAcademy.learnMore)}
            </Link>
          </Typography>
        </SignInWrapper>
      </div>
    </FormWrapper>
  );
};

export { OIDCSignInBlock };
